// src/components/lessons/renderers/TableRenderer.jsx
import React, { useState } from 'react';

const TableRenderer = ({ block, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  const processText = (text) => {
    if (typeof text !== 'string') return text;
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, index) => {
      return index % 2 === 1 ? (
        <strong key={index} style={{ fontWeight: '700', color: '#000000' }}>
          {part}
        </strong>
      ) : part;
    });
  };

  const headers = block.headers || [];
  const rows = block.rows || [];

  // Mobile: card layout so all content is visible (no horizontal scroll / clipped cells)
  if (isMobile && rows.length > 0) {
    return (
      <div
        style={{
          border: '2px solid #000000',
          borderRadius: '0.5rem',
          overflow: 'hidden',
          marginTop: '1rem',
          marginBottom: '1rem',
          background: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          position: 'relative'
        }}
      >
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: '4px',
          background: '#3A86FF',
          zIndex: 10
        }} />

        {block.title && (
          <div style={{
            background: '#000000',
            padding: '0.625rem 0.75rem',
            fontWeight: '700',
            fontSize: '0.875rem',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem'
          }}>
            <div style={{
              width: '5px', height: '5px',
              borderRadius: '50%',
              background: '#3A86FF',
              flexShrink: 0
            }} />
            <span style={{ wordBreak: 'break-word' }}>{processText(block.title)}</span>
          </div>
        )}

        <div style={{ padding: '0.25rem 0' }}>
          {rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                margin: '0 0.5rem 0.5rem',
                padding: '0.625rem 0.75rem',
                background: '#FAFBFC',
                border: '1px solid #E5E7EB',
                borderRadius: '0.375rem',
                borderLeft: '3px solid #3A86FF'
              }}
            >
              {row.map((cell, cellIndex) => {
                const label = headers[cellIndex] != null ? headers[cellIndex] : `Column ${cellIndex + 1}`;
                return (
                  <div
                    key={cellIndex}
                    style={{
                      marginBottom: cellIndex < row.length - 1 ? '0.5rem' : 0,
                      minWidth: 0,
                      wordBreak: 'break-word'
                    }}
                  >
                    <div style={{
                      fontSize: '0.6875rem',
                      fontWeight: '700',
                      color: '#6b7280',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      marginBottom: '0.25rem'
                    }}>
                      {processText(label)}
                    </div>
                    <div style={{
                      fontSize: '0.9375rem',
                      lineHeight: 1.45,
                      color: '#000000'
                    }}>
                      {processText(cell)}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Desktop: table with horizontal scroll so wide tables don't clip
  return (
    <div
      style={{
        border: '2px solid #000000',
        borderRadius: '0.75rem',
        overflow: 'hidden',
        marginTop: '1.5rem',
        marginBottom: '1.5rem',
        background: '#FFFFFF',
        boxShadow: isHovered ? '0 8px 24px rgba(0, 0, 0, 0.12)' : '0 2px 8px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: '4px',
        background: '#3A86FF',
        zIndex: 10
      }} />

      {block.title && (
        <div style={{
          background: '#000000',
          padding: '1rem 1.25rem',
          fontWeight: '700',
          color: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <div style={{
            width: '6px', height: '6px',
            borderRadius: '50%',
            background: '#3A86FF'
          }} />
          {block.title}
        </div>
      )}

      <div
        style={{
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          minWidth: 0
        }}
      >
        <table style={{
          width: '100%',
          minWidth: headers.length > 0 ? `${Math.max(200, headers.length * 120)}px` : '100%',
          borderCollapse: 'collapse',
          fontSize: '0.95rem',
          background: '#FFFFFF'
        }}>
          {headers.length > 0 && (
            <thead>
              <tr style={{
                background: '#FAFBFC',
                borderBottom: '2px solid #000000'
              }}>
                {headers.map((header, i) => (
                  <th key={i} style={{
                    padding: '1rem',
                    textAlign: 'left',
                    fontWeight: '700',
                    color: '#000000',
                    borderRight: i !== headers.length - 1 ? '1px solid #E5E7EB' : 'none',
                    whiteSpace: 'nowrap'
                  }}>
                    {processText(header)}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  borderBottom: rowIndex === rows.length - 1 ? 'none' : '1px solid #E5E7EB',
                  background: hoveredRow === rowIndex ? '#F9FAFB' : '#FFFFFF',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={() => setHoveredRow(rowIndex)}
                onMouseLeave={() => setHoveredRow(null)}
              >
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} style={{
                    padding: '1rem',
                    color: '#000000',
                    opacity: hoveredRow === rowIndex ? 1 : 0.85,
                    transition: 'opacity 0.2s ease',
                    borderRight: cellIndex !== row.length - 1 ? '1px solid #E5E7EB' : 'none',
                    position: 'relative',
                    verticalAlign: 'top',
                    wordBreak: 'break-word',
                    maxWidth: '320px'
                  }}>
                    {hoveredRow === rowIndex && cellIndex === 0 && (
                      <div style={{
                        position: 'absolute',
                        left: 0, top: 0, bottom: 0,
                        width: '3px',
                        background: '#3A86FF'
                      }} />
                    )}
                    {processText(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isHovered && (
        <div style={{
          position: 'absolute',
          bottom: '12px', right: '12px',
          width: '6px', height: '6px',
          borderRadius: '50%',
          background: '#3A86FF',
          animation: 'pulse 1.5s ease-in-out infinite',
          zIndex: 10
        }} />
      )}
    </div>
  );
};

export default TableRenderer;